/* ============================================================================
   THE INTERVENTION LADDER                                            (PRD 4)

   Five moves, taken one at a time, and the numbering is BACK after being removed.

   ── THE REVERSAL, BECAUSE IT MATTERS THAT THIS IS ONE ──────────────────────
   On 2026-09-15 the ladder's numbering came off. The reason was good: staff read
   "Rung 3" as a rank, and a care model must not rank people. The moves stayed and
   only the numbers went.

   PRD 4 puts them back, and answers the objection rather than ignoring it:

     · it is a STEP, not a rung, and a step is a position in the GYM'S OWN
       SEQUENCE OF MOVES rather than a reading on the member;
     · it is always written with its denominator, "Step 3 of 5", because a bare
       number beside a person's face reads as a level of that person and the
       denominator is what makes it a sequence on screen instead;
     · it ATTACHES TO THE ACTION. It appears on the line that says what to do
       next - "the next step for Maria is an invitation" - and never as a badge
       beside her name, never as a column, and never as something sortable.

   This file can only produce the labelled form. There is no exported function
   that returns a bare number, which is the cheapest way to make the rule hold.

   ── THE STEP COMES FROM THE SITUATION, THE TIER FROM THE SCORE ─────────────
   Two members on identical scores can be on different steps, and a member's step
   moves the moment the gym acts. The one hard rule PRD 4 states outright: a member
   with NO ANCHOR starts at Step 3 whatever their tier, because the first two steps
   are a touch from somebody and there is nobody to send it.

   ── THE MODEL NEVER PROPOSES STEP 5 ────────────────────────────────────────
   Concession is commercial terms: Owner only, always paired with a reason, always
   through the approval queue, and "never framed as a retention offer or as a
   reaction to a threat". A model that offered it the moment somebody drifted would
   be doing all three of those things. So the furthest this file will go on its own
   is Step 4, and Step 5 is a human act with a signature behind it.
   ============================================================================ */
export const LADDER_STEPS = 5;
/* The five, with the test each one has to pass. The tests are from PRD 4 and are
   kept here because they are what stops a step being renamed into meaninglessness. */
export const LADDER = {
    1: {
        name: 'Notice',
        test: 'They could reasonably not reply and nothing is awkward.',
    },
    2: {
        name: 'Curiosity',
        test: 'Specific enough about them that a generic answer is impossible.',
    },
    3: {
        name: 'Invitation',
        test: 'A specific occasion with a name attached, not a promotion.',
    },
    4: {
        name: 'Accommodation',
        test: 'Change the gym, not the price. It addresses the actual obstacle.',
    },
    5: {
        name: 'Concession',
        test: 'Commercial terms. Owner only, with a reason, through the queue.',
    },
};
/** "Step 3 of 5". The only way this file will render a step. */
export function stepLabel(step) {
    return `Step ${step} of ${LADDER_STEPS}`;
}
/**
 * The next move for this member, or null when the answer is to leave them alone.
 *
 * Green with nothing wrong gets NO STEP, and that is a real answer rather than a
 * gap: manufacturing contact with somebody who is fine is its own harm, and a
 * product that always has a next action will always be ignored.
 */
export function nextStep(input) {
    const anchors = Array.isArray(input.anchors) ? input.anchors : [];
    const modelsAnchors = input.modelsAnchors ?? true;
    const hasAnchor = anchors.length > 0;
    const unanswered = Math.max(0, Number(input.unansweredOutreach) || 0);
    /* NOBODY IS FINE AND ALSO ON THE LADDER. */
    if (input.tier === 'Green' && (hasAnchor || !modelsAnchors))
        return null;
    /* On a tenant that does not model anchors the move must not claim a relationship
       the gym has never recorded, so the wording softens rather than the step moving. */
    const from = hasAnchor ? 'the strongest existing anchor' : 'whoever knows them best';
    /* THE ONE RULE PRD 4 STATES OUTRIGHT. No anchor starts at Step 3, at any tier,
       because the first two steps are a touch FROM somebody and there is nobody to
       send it. This is checked before the tier, which is what "whatever their tier"
       means. */
    let step;
    let because;
    let move;
    if (modelsAnchors && !hasAnchor) {
        step = 3;
        because = 'Nobody here knows them, so there is nobody to send a quieter touch from.';
        move = 'No named anchor yet. Introduce them to a named person in the room: '
            + 'an invitation, not a message.';
    }
    else if (input.tier === 'Red') {
        step = 2;
        because = 'They have drifted far enough that a signal on its own will not reach them.';
        move = `A phone call from ${from}, with a genuine question about them rather than `
            + 'their membership. Ask what changed, then stop talking.';
    }
    else {
        step = 1;
        because = 'Early enough that being noticed is the whole intervention.';
        move = `Warm, non-transactional touch within 72 hours, from ${from}. `
            + 'No call to action, and nothing they have to reply to.';
    }
    /* ESCALATION IS EVIDENCE, NOT TIME. The gym has tried and heard nothing back, so
       the next move changes the gym rather than repeating the message. Capped at 4:
       Concession is a human act with a signature, never something a model proposed. */
    if (unanswered > 0 && step < 4) {
        step = 4;
        because = unanswered === 1
            ? 'One touch has gone unanswered, so repeating it is not the next move.'
            : `${unanswered} touches have gone unanswered, so repeating them is not the next move.`;
        move = 'Change the gym, not the price: move a session, hold a slot, reassign a coach, '
            + 'or work around whatever is actually in the way.';
    }
    return { step, name: LADDER[step].name, label: stepLabel(step), move, because };
}
/** The test a move at this step has to pass, for the screen that asks somebody to write one. */
export function stepTest(step) {
    return LADDER[step].test;
}
